/* product.js — PDP-specific: gallery carousel (stage + thumbs), image zoom/pan,
 * gallery sync on variant change. Variant switching itself lives in ui.js
 * (shared with quick view). */
(() => {
  'use strict';
  const section = document.querySelector('.product[data-section-id]');
  if (!section) return;

  /* ---------- gallery carousel ---------- */
  const gal = section.querySelector('[data-gallery]');
  const stage = gal && gal.querySelector('[data-stage]');
  if (stage) {
    /* --- variant-scoped media (Luke 2026-09-10) ---------------------------
     * "when a variant swatch is selected, it's related images are the only
     * ones that are shown in their image gallery carousel... Ours shows all
     * possible images related to that configurable product."
     *
     * sections/main-product.liquid does the FIRST render of this filter
     * server-side (so a deep link to a variant arrives correct, with no flash
     * of the full set and no JS-time correction the CLS gate would see). This
     * re-applies exactly the same rule on variant:change, reading only the two
     * attributes the section wrote: `data-variant-owned` (some variant claims
     * this media) and `data-media-id`.
     *
     * Rule: show the selected variant's own media, plus media no variant
     * claims (technical drawings, dimension diagrams, lifestyle shots,
     * videos). Hide media another variant claims. When the selected variant
     * owns nothing, show everything — the fallback that keeps a sparse product
     * off an empty gallery, and it is 15% of multi-variant products.
     *
     * `allSlides` / `allThumbs` never change; `slides` / `thumbs` are the
     * VISIBLE set and are rebuilt on every filter change. Everything below —
     * idx, the counter, the arrows, the thumb pairing, the scroll snap
     * arithmetic, and the lightbox through gallery.visibleSlides() — counts in
     * the visible set, so all of them agree on n / N by construction. */
    const allSlides = [...stage.children];
    const allThumbs = [...gal.querySelectorAll('[data-thumb]')];
    const thumbsWrap = gal.querySelector('[data-thumbs]');
    /* Is this a product whose media is variant-scoped at all?
     *
     * This used to read `gal.hasAttribute('data-variant-scoped')`, and the
     * section sets that attribute only when the INITIALLY SELECTED variant
     * owns media (`scope_media` in sections/main-product.liquid). That makes a
     * PRODUCT-level property depend on which variant happened to be default —
     * and the default is Shopify's first AVAILABLE variant, which moves when
     * stock moves. On a product where the default variant owns nothing but
     * others do (15% of multi-variant products, by the section's own census)
     * the whole session then ran unscoped: clicking a finish jumped the stage
     * to that finish's photo but left every other finish's photo in the strip,
     * which is the opposite of what the rule was written for — Luke,
     * 2026-09-10: "when a variant swatch is selected, it's related images are
     * the only ones that are shown in their image gallery carousel."
     *
     * `data-variant-owned` is written by the section for every media some
     * variant claims, INDEPENDENT of `scope_media` (read the slide loop: the
     * `owned` test and the `scope_media` test are separate). So the presence
     * of any owned slide is exactly the product-level question, and it is
     * stable whatever the default variant is.
     *
     * The first paint is unchanged either way: applyScope is still not called
     * at load, so this only decides what happens on the first variant:change.
     * The sparse-product fallback is untouched — it lives per variant, in the
     * handler below, where a variant owning no media re-shows everything. */
    const scoped = gal.hasAttribute('data-variant-scoped')
      || allSlides.some((el) => el.hasAttribute('data-variant-owned'));
    let slides = allSlides.filter((el) => !el.hidden);
    let thumbs = allThumbs.filter((el) => !el.hidden);
    const prev = gal.querySelector('[data-gprev]');
    const next = gal.querySelector('[data-gnext]');
    const count = gal.querySelector('[data-gcount]');
    let idx = 0;

    // slides and thumbs are rendered in the SAME media order, so pairing them
    // by position in the visible list needs no id lookup.
    const applyScope = (mediaId) => {
      const key = mediaId ? String(mediaId) : '';
      const hide = (el) => (key ? el.hasAttribute('data-variant-owned') && el.dataset.mediaId !== key : false);
      allSlides.forEach((el) => { el.hidden = hide(el); });
      allThumbs.forEach((el) => { el.hidden = hide(el); });
    };

    const refresh = () => {
      slides = allSlides.filter((el) => !el.hidden);
      thumbs = allThumbs.filter((el) => !el.hidden);
      // one image left: the arrows and the "1 / 1" counter say nothing, and a
      // single lone thumbnail under a single photo is noise. They are hidden,
      // not removed — the next variant may bring the set back above one.
      const many = slides.length > 1;
      [prev, next, count].forEach((el) => { if (el) el.hidden = !many; });
      if (thumbsWrap) thumbsWrap.hidden = !many;
      thumbs.forEach((t, j) => t.setAttribute('aria-label', 'Image ' + (j + 1) + ' of ' + slides.length));
    };

    /* Bring the active thumbnail into view inside the thumbnail RAIL — and
     * nowhere else.
     *
     * Luke 2026-09-12: "every time I click an attribute value swatch, the
     * page scrolls down, this is unwanted behaviour." This line was it:
     *
     *   at.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: 'smooth' })
     *
     * scrollIntoView is not a scroll of one box. It walks EVERY scrollable
     * ancestor of the element up to and including the document and scrolls
     * each one until the element is in view. `block: 'nearest'` does not opt
     * out of the vertical axis; it only promises to do nothing vertically
     * when the element is ALREADY fully visible vertically. The rail sits
     * under a square stage and is routinely a hundred-odd pixels short of
     * that, so the browser finished the job by scrolling the window — which
     * is exactly the jump in the report.
     *
     * Measured on this branch's ci theme 165463064611 (run 34658641552),
     * every finish swatch on caroma-liano-ii-basin-bath-outlet-clearance:
     * scrollY 0 -> 109 at 1440 and 523 -> 411 at 390, on all four swatches
     * and on the keyboard path, with the instrumented pass naming this call
     * (and only this call) as the one that moved the page.
     *
     * Scrolling the rail by hand cannot reach the window: one element, one
     * axis, no ancestor walk. `over` is 0 whenever the thumb is already
     * inside the rail, so the common case moves nothing at all.
     */
    const reduceMotion = () => !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
    const revealThumb = (el) => {
      if (!el || !thumbsWrap || el.hidden || thumbsWrap.hidden) return;
      const r = el.getBoundingClientRect();
      const w = thumbsWrap.getBoundingClientRect();
      if (!r.width || !w.width) return; // rail not laid out (hidden, or pre-paint)
      const over = r.left < w.left ? r.left - w.left : (r.right > w.right ? r.right - w.right : 0);
      if (!over) return;
      thumbsWrap.scrollTo({ left: thumbsWrap.scrollLeft + over, behavior: reduceMotion() ? 'auto' : 'smooth' });
    };

    const setActive = (i) => {
      idx = Math.max(0, Math.min(slides.length - 1, i));
      thumbs.forEach((t, j) => {
        t.classList.toggle('is-active', j === idx);
        t.setAttribute('aria-selected', j === idx ? 'true' : 'false');
      });
      if (count) count.textContent = (idx + 1) + ' / ' + slides.length;
      if (prev) prev.disabled = idx === 0;
      if (next) next.disabled = idx === slides.length - 1;
      if (!slides.length) return;
      revealThumb(thumbs[idx]);
    };
    // behavior: 'smooth' for anything the shopper watches happen; 'auto' when
    // the caller is restoring position behind an overlay that is closing this
    // frame (assets/pdp-lightbox.js) and an animation would be invisible.
    const goTo = (i, behavior) => {
      const n = Math.max(0, Math.min(slides.length - 1, i));
      const s = slides[n];
      if (!s) return;
      stage.scrollTo({ left: s.offsetLeft, behavior: behavior || 'smooth' });
      // An instant jump settles NOW, so claim the index now rather than waiting
      // for a scroll event that an already-correct scrollLeft never fires. A
      // smooth scroll is left to the scroll listener, which tracks it frame by
      // frame (and covers touch swipes the same way).
      if (behavior === 'auto') setActive(n);
    };

    /* Which slide is the stage showing? From the slides' REAL positions, never
     * from an assumed uniform pitch.
     *
     * This used to be Math.round(stage.scrollLeft / stage.clientWidth), which
     * assumes every slide is exactly one clientWidth wide and the first starts
     * at 0. Neither holds: .pgal__slide is flex:0 0 100% with flex-shrink 0,
     * and measured on the ci preview 2026-09-10 each slide sits ~100px WIDER
     * than stage.clientWidth at BOTH 1440 (pitch ~799.5 vs clientWidth 699)
     * and 390 (pitch 443 vs 343). The error compounds with the index, so the
     * old formula was right near slide 0 and wrong later — at 390 it read
     * scrollLeft 886 / clientWidth 343 = round(2.58) = slide 3 while the stage
     * was showing slide 2. That is a drift in main's counter on every touch
     * swipe past the first couple of images, not something this branch
     * introduced; the lightbox's close only made it reproducible, because it
     * jumps to an exact slide and then asks which slide that was.
     *
     * Nearest-offsetLeft is exact under any pitch, gap, padding or filtered
     * set, and costs one pass over a handful of elements per animation frame.
     */
    const indexFromScroll = () => {
      let best = 0, bestD = Infinity;
      for (let i = 0; i < slides.length; i++) {
        const d = Math.abs(slides[i].offsetLeft - stage.scrollLeft);
        if (d < bestD) { bestD = d; best = i; }
      }
      return best;
    };

    // active slide tracks scroll position (covers touch swipes too)
    let raf = null;
    stage.addEventListener('scroll', () => {
      if (raf) return;
      raf = requestAnimationFrame(() => {
        raf = null;
        const i = indexFromScroll();
        if (i !== idx) setActive(i);
      });
    }, { passive: true });

    // bound on EVERY thumb once, but the index is resolved at click time
    // against the current visible list — `thumbs` is a different array after
    // each variant change.
    allThumbs.forEach((t) => t.addEventListener('click', () => {
      const i = thumbs.indexOf(t);
      if (i > -1) goTo(i);
    }));
    if (prev) prev.addEventListener('click', () => goTo(idx - 1));
    if (next) next.addEventListener('click', () => goTo(idx + 1));
    stage.addEventListener('keydown', (e) => {
      if (e.key === 'ArrowRight') { e.preventDefault(); goTo(idx + 1); }
      if (e.key === 'ArrowLeft') { e.preventDefault(); goTo(idx - 1); }
    });
    // First paint reads the DOM as the section rendered it — applyScope is NOT
    // called here. Liquid already applied the identical rule server-side, and
    // re-running it in JS on load could only either agree (a wasted reflow) or
    // disagree (a visible jump the CLS gate would catch).
    refresh();
    setActive(0);

    /* ---------- the gallery's public surface (assets/pdp-lightbox.js) --------
     * The full-screen viewer is a VIEW onto this carousel, not a second one:
     * it opens on `index`, and on close calls `goTo` so the strip behind lands
     * on whatever the shopper last looked at. Publishing three members on the
     * element is the whole contract — deliberately not a custom-element class,
     * which would mean upgrading <product-gallery> and re-homing all of this.
     * `visibleSlides` (not `slides`) is what callers should read: it is the
     * set the shopper can actually reach right now. */
    gal.gallery = {
      allSlides,
      // the set the shopper can actually reach right now, after variant
      // scoping. `index` and `goTo` both count in THIS list, not in allSlides.
      visibleSlides: () => slides,
      get index() { return idx; },
      goTo,
    };

    // variant change -> re-scope the media, then land on that variant's own
    // photo (no page scroll). Order matters: the filter runs FIRST so the
    // index we then jump to is an index in the new visible set.
    section.addEventListener('variant:change', (e) => {
      const m = e.detail && e.detail.featured_media;
      if (scoped) { applyScope(m && m.id); refresh(); }
      if (!m) { if (scoped) { goTo(0, 'auto'); setActive(0); } return; }
      const i = slides.findIndex((s) => s.dataset && s.dataset.mediaId === String(m.id));
      if (i < 0) return;
      // 'auto', not 'smooth': hiding slides collapses the strip, so the
      // scroll position is already meaningless and animating from it would
      // slide the shopper across images that are no longer there. setActive
      // is called explicitly because an instant scrollTo to a position the
      // stage may already hold fires no scroll event.
      goTo(i, scoped ? 'auto' : 'smooth');
      setActive(i);
    });

    // aggressive warming: lazy slides only fetch when scrolled near — flip
    // them eager at idle so arrows/thumbs/variant jumps land on a hot cache
    const idle = (fn) => ('requestIdleCallback' in window ? requestIdleCallback(fn, { timeout: 2000 }) : setTimeout(fn, 400));
    // allSlides, not the visible set: a variant change reveals slides that were
    // hidden at load, and they should already be warm when it does.
    idle(() => allSlides.forEach((s, i) => {
      if (i === 0) return; // hero already prioritised
      s.querySelectorAll('img[loading="lazy"]').forEach((img) => { img.loading = 'eager'; });
    }));
  }

  /* ---------- in-place zoom: REMOVED 2026-09-10, folded into the lightbox ----
   * There used to be a .zoom-toggle on every image slide that added .is-zoomed
   * and scaled the slide's <img> to 2.2 in place, panning on mousemove. It is
   * gone, and assets/pdp-lightbox.js owns zoom now. Reasons, in order:
   *   1. It magnified pixels it did not have. The slide's source is the
   *      display rendition (960px src, sizes 52vw) — scaling that 2.2x showed
   *      a blurrier picture, which is the opposite of what a zoom is for. The
   *      lightbox loads a 2048/2560px rendition, so its zoom resolves detail.
   *   2. It was pointer-only: the pan ran off `mousemove`, so on a phone the
   *      control zoomed and then could not be steered.
   *   3. Two zoom affordances on one image is worse than one. The magnifier
   *      that replaced it is the single "look closer" control on the slide.
   * The .zoomable / .zoomable__img class names are kept: they are the layout
   * hooks .pgal__slide .zoomable__img styles against, not zoom state. */

  /* ---------- quantity stepper: MOVED to assets/ui.js (2026-09-21) --------
   * The -/+ buttons, arrow keys, typed input and its normalisation, the
   * inventory cap, the Add-to-cart price label (qty x unit) and the reset
   * after a confirmed add used to live right here, scoped to
   * `.product[data-section-id]` — which this file returns early on. That made
   * the whole behaviour PDP-only by construction, and the quick view (the
   * same buy box, rendered through the Section Rendering API into a modal)
   * silently had none of it: no stepper, no price on the button. Luke,
   * 2026-09-21: "add to cart in quick view should have the amount just like
   * the product page itself."
   *
   * It is now `initBuyRow(root)` in ui.js, called for the PDP at boot and
   * again for the quick view's subtree each time that subtree is rendered.
   * ui.js is the right home: the variant switcher immediately above it is
   * already the shared PDP + quick view code, and a controller that has to
   * bind to markup which ARRIVES AFTER LOAD cannot be a boot-time block in a
   * file the quick view never loads. Nothing about the stepper's behaviour
   * changed in the move. This file keeps what is genuinely PDP-only: the
   * gallery carousel and the reviews jump. */

  /* ---------- rating row -> reviews section jump ----------
   * The links in snippets/rating-summary.liquid are real `href="#reviews"`
   * anchors, so with JS off (or before this file runs) the browser already
   * navigates and focuses the target itself. This only UPGRADES that:
   *   - smooth scroll, unless the visitor asked for reduced motion;
   *   - focus() with preventScroll so the smooth scroll is not cut short by
   *     the browser's own instant jump to the focused element. Focus is the
   *     point: a scroll alone leaves a screen-reader user and a keyboard
   *     user where they were, and the next Tab would resume at the top.
   *   - the hash still lands in the URL (replaceState, not pushState: the
   *     jump is a move within one page, not a new history entry to back out
   *     of, and Back should return to the collection the shopper came from).
   * The target is looked up document-wide because the reviews section is a
   * sibling of `.product`, not a child of it. */
  document.addEventListener('click', (e) => {
    const jump = e.target.closest('[data-reviews-jump]');
    if (!jump) return;
    const id = (jump.getAttribute('href') || '').replace(/^#/, '');
    if (!id) return;
    const target = document.getElementById(id);
    if (!target) return; // no reviews section rendered: let the anchor be inert
    e.preventDefault();
    const reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    try { target.focus({ preventScroll: true }); } catch { target.focus(); }
    target.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', block: 'start' });
    try { history.replaceState(null, '', '#' + id); } catch {}
  });
})();
